import { Timestamp } from '@bufbuild/protobuf';
import {
  ImageWrapper,
  InfoDisplayPanel,
  NewSectionList,
  deserify,
  firstLetterImage,
  getDifficultyLevelString,
  getHumanReadableTimestampString,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
  theme,
  setLocalStorage,
  getEnvConfig,
  IClassAndSubjectSelected,
  AssessmentCard,
  ShimmerActiveHwCard,
  AssessmentTopicPopup,
  SectionListWithDotCarousel,
} from '@geneo2-web/shared-ui';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { HomeworkTask } from '@protos/learning_management/lms.hw.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  ASSESSMENT_CURRENT,
  HOMEWORK,
  HOMEWORK_COMPLETED,
  HOMEWORK_ONGOING,
} from '../../../routeHandling/RoutesNomenclature';
import { getSubjectsMap } from '../../../utils/icons';
import { interactionEvent } from '../../Auth/auth.events';
import {
  resetCreatedAssessmentStatus,
  resetSelectedTasksInfo,
  setCreatedAssessmentStatus,
  setFetchedAssessmentDetails,
  setModuleFilteredQuestions,
  setSelectedTasksInfo,
} from '../../Assessment/reducer/assessmentFlow.slice';
import { setSelectedAssessmentId } from '../../ManageHomework/reducer/manageHomework.slice';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import {
  AssessmentEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { HomeworkListShimmer } from '../shimmer';
import { setTeacherClickerAssessmentList } from '../reducer/homeDashboard.slice';
import { convertToSequenceInfo } from '../../Assessment/Common/ReviewAssessment/functions';
import { getSubjectListForAssessmentCard } from '../../Assessment/utils/assessment.utils';
interface IProps {
  seeAllClickHandler: () => void;
}
export default function HomeClickerAssessmentList(props: IProps) {
  const { seeAllClickHandler } = props;
  const maxItemToShow = 20;
  const navigate = useNavigate();
  const { isOffline } = useDownloadContext();
  const dispatch = useAppDispatch();
  const teacher_profile_id = deserify(getLocalStorage('userId'));
  const { assessment_list_data } = useAppSelector(
    (state) => state.manageHomework
  );
  const prevClassSubjectInfo = useRef<IClassAndSubjectSelected | undefined>(
    undefined
  );
  const isFirstRender = useRef(false); // Tracks if it's the initial render
  const [assessmentListLoading, setAssessmentListLoading] = useState(false);
  const [assessmentListError, setAssessmentListError] =
    useState<boolean>(false);
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { class_subject_info, teacher_clicker_hw_list } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const [selectedHomework, setSelectedHomework] = useState<
    AssessmentTask | undefined
  >();
  const [chapterListPopup, setChapterListPopup] = useState(false);
  const location = useLocation();
  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
  } = useCommonServiceClientContext();

  useEffect(() => {
    dispatch(resetSelectedTasksInfo());
    dispatch(resetCreatedAssessmentStatus());
  }, []);

  const ChapterListPopupHandler = (assessment: AssessmentTask) => {
    setSelectedHomework(assessment);
    setChapterListPopup(true);
  };

  const cardClickHandler = async (
    homeworkId: number,
    homeworkTargetDate?: Timestamp,
    moduleId?: number
  ) => {
    if (!homeworkTargetDate && moduleId) {
      getHomeworkDetails(homeworkId, moduleId.toString());
      return;
    }
    if (!homeworkTargetDate) return;

    await interactionEvent({
      url: 'Teacher_Home',
      context: 'Homeworks',
      name: 'HOMEWORK_OPEN',
      isOffline: isOffline,
    });
    dispatch(setSelectedAssessmentId(homeworkId));
    navigate('/clicker-assessment-manage', {
      state: {
        from: location.pathname,
      },
    });
  };

  const getHomeworkDetails = async (homeworkId: number, moduleId: string) => {
    try {
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_profile_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: homeworkId,
          }
        );
      if (response.data) {
        const HwData = response.data;
        dispatch(
          setCreatedAssessmentStatus(HwData.assessment?.taskCreationStatus)
        );
        dispatch(
          setModuleFilteredQuestions({ questions: HwData.assessmentContent })
        );
        dispatch(setFetchedAssessmentDetails(HwData.assessment));
        const sequenceInfo = convertToSequenceInfo(
          HwData.questionsSequenceInfo
        );
        if (sequenceInfo) {
          dispatch(setSelectedTasksInfo(sequenceInfo));
        }
        navigate(`${HOMEWORK}${ASSESSMENT_CURRENT}/${moduleId}`);
      }
    } catch (err) {
      console.log(err);
    }
  };
  function hasTimePassed(milliseconds: string) {
    const currentTime = new Date().getTime();
    const targetTime = Number(milliseconds);
    return targetTime <= currentTime;
  }
  const getTeacherAssessmentList = async (teacherId: string) => {
    try {
      setAssessmentListLoading(true);
      setAssessmentListError(false);
      const response =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.getTeacherAssessmentsList(
          {
            teacherId: BigInt(teacherId),
            subjectId: class_subject_info?.subjectId,
            sectionId: class_subject_info?.sectionId,
            assessmentType: AssessmentEnum.ASSESSMENT_UNDEFINED,
            modeOfConduction:
              ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER,
          }
        );
      const data = response.assessmentList;
      if (data?.assessmentList) {
        const ClickerAssignedlist = [
          ...data.assessmentList.assigned,
          // ...data.homeworkList.ended,
        ];

        dispatch(setTeacherClickerAssessmentList(ClickerAssignedlist));
      } else {
        dispatch(setTeacherClickerAssessmentList(undefined));
        setAssessmentListError(true);
        // setHomeworkList([]);
      }
      setAssessmentListLoading(false);
    } catch (err) {
      setAssessmentListLoading(false);
      setAssessmentListError(true);
      dispatch(setTeacherClickerAssessmentList(undefined));
      console.log(err);
    }
  };

  useEffect(() => {
    if (!isFirstRender.current) {
      // Skip the first render
      isFirstRender.current = true;
      prevClassSubjectInfo.current = class_subject_info; // Initialize the ref
      return;
    }

    // Convert objects to strings for deep comparison
    const prevValueString = JSON.stringify(prevClassSubjectInfo.current);
    const currentValueString = JSON.stringify(class_subject_info);
    if (prevValueString !== currentValueString) {
      getTeacherAssessmentList(teacher_profile_id);
    }

    // Update the ref to the current value
    prevClassSubjectInfo.current = class_subject_info;
  }, [class_subject_info]);
  useEffect(() => {
    getTeacherAssessmentList(teacher_profile_id);
  }, []);
  return assessmentListLoading === true ? (
    <HomeworkListShimmer children={ActiveHwShimmerArray} />
  ) : (
    <Box>
      <SectionListWithDotCarousel
        noContentMessage="No Assessments for Today"
        isError={assessmentListError}
        backgroundColor="#FFF7D5"
        containerMdWidth="1214"
        itemsToShow={3}
        maxItemsToShow={maxItemToShow}
        title="Clicker Assessments"
        linkButtonHandler={seeAllClickHandler}
        linkButtonText="See All"
        totalNoOfItems={
          assessment_list_data?.assesmentListInfo?.clickerAssessmentsCount
        }
        items={teacher_clicker_hw_list
          ?.filter(
            (item) =>
              item.modeOfConduction ===
              ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
          )
          .slice(0, maxItemToShow)
          .map((hw) => (
            <AssessmentCard
              homework={hw}
              assessCardType="Assigned"
              hideDeleteIcon
              chapterListPopupHandler={ChapterListPopupHandler}
              modeofConduction
              onClick={() =>
                cardClickHandler(
                  hw.assessmentId,
                  new Timestamp(hw.assessmentTargetDate),
                  hw.moduleId
                )
              }
              rootStyles={{
                width: { xs: pxToRem(320), md: pxTovW(590), lg: pxTovW(464) },
              }}
              subjectList={getSubjectListForAssessmentCard(
                hw,
                user_info?.teachClassSubjects
              )}
            />
          ))}
      />
      <AssessmentTopicPopup
        assessment={selectedHomework}
        open={chapterListPopup}
        handleClose={() => setChapterListPopup(false)}
      />
    </Box>
  );
}
const ActiveHwShimmerArray = [
  <ShimmerActiveHwCard key={1} variant="large" />,
  <ShimmerActiveHwCard key={2} variant="large" />,
];
interface InfoDisplayPanelStatus {
  lesson: AssessmentTask;
}
const LessonPlanInfoPanelStatus = ({ lesson }: InfoDisplayPanelStatus) => {
  // const taughtTime = getHumanReadableTimestampString(
  //   lesson.lastSessionTime
  // )?.split(' ');
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const editTime = getHumanReadableTimestampString(
    lesson.lastModifiedTime
  )?.split(' ');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: `${pxTovW(2)} solid #E7E7E7D9`,
        pt: { xs: pxToRem(5), md: pxTovW(5) },
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: pxToRem(5), md: pxTovW(5) },
        }}
      >
        <ImageWrapper
          name="geneo-blue"
          type="png"
          parentFolder="icons"
          styles={{
            width: { xs: pxToRem(18), md: pxTovW(28) },
            height: { xs: pxToRem(18), md: pxTovW(28) },
            borderRadius: '50%',
          }}
          path={
            getMediaBasePath(lesson.teacherProfileImageUrl) ||
            firstLetterImage(lesson.teacherName) ||
            firstLetterImage('Geneo')
          }
        />
        <Typography variant="smallText" fontWeight="bold">
          {user_info?.teacherProfileId.toString() ===
          lesson?.teacherId?.toString()
            ? 'You'
            : lesson.teacherName || 'Geneo'}
        </Typography>
      </Box>

      {lesson.teacherName && (
        <Typography variant="subText" color="text.disabled">
          {editTime && Number(editTime[0]) < 2
            ? 'Recently Edited'
            : 'Edited ' + editTime?.join(' ')}
        </Typography>
      )}
    </Box>
  );
};
